import { useQueryClient } from '@tanstack/react-query'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { AppShell } from './app/AppShell'
import { Spinner } from '@bb/ui'
import { LoginPage } from './features/auth/LoginPage'
import { useLogout, useSession } from './features/auth/useSession'
import { DashboardPage } from './features/dashboard/DashboardPage'
import { SettingsPage } from './features/settings/SettingsPage'
import { UsersPage } from './features/users/UsersPage'

export function AppRouter() {
  const { data: user, isPending } = useSession()
  const logout = useLogout()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  if (isPending) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-sheet">
        <Spinner className="size-6" />
      </div>
    )
  }

  /**
   * Design §3: `isAdmin` is what grants the shell, not merely having a
   * session. A session that resolves but is not an administrator's — most
   * plausibly a stray cookie from a login LoginPage already refused and
   * logged back out — is treated exactly like being signed out, so there is
   * no state in this router that shows an authenticated-but-not-admin
   * caller anything a signed-out caller would not also see.
   */
  if (!user?.isAdmin) {
    return (
      <Routes>
        <Route path="/anmelden" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/anmelden" replace />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route
        element={
          <AppShell
            userName={`${user.firstName} ${user.lastName}`}
            userEmail={user.email}
            // A deliberate, navigated-away-from sign-out: unlike LoginPage's
            // own use of this same mutation (§3's non-admin rejection,
            // which must NOT lose its message — see useLogout's own
            // comment), this is the one call site that should also drop
            // every other cached query, so no admin data lingers in memory
            // once the shell is gone.
            onLogout={() => logout.mutate(undefined, {
              onSuccess: () => {
                queryClient.clear()
                navigate('/anmelden')
              },
            })}
          />
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="nutzer" element={<UsersPage />} />
        <Route path="einstellungen" element={<SettingsPage />} />
      </Route>
      <Route path="/anmelden" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
